import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';

import { observeElementResize } from '#/lib/observe-element-resize';
import { cn } from '#/lib/utils';

import { TranscendLogo } from '../TranscendLogo';

gsap.registerPlugin(useGSAP, ScrollTrigger);

/**
 * The scene is authored on a fixed 846x543 canvas (matching the Figma frames)
 * and scaled uniformly to fit its container. The mobile design (457:21356)
 * is this same canvas at ~42% scale, annotations included.
 */
const SCENE_W = 846;
const SCENE_H = 543;

/** Same projection Figma applies to every isometric plate. */
const ISO_TRANSFORM = 'rotate(-30deg) skewX(30deg) scaleY(0.87)';

/** Plate fill from the diagram file (differs slightly from site cipher-07). */
const TRANSCEND_PLATE_COLOR = '#403EA0';

const SYSTEM_LOGOS = [
  ['workday', 'openai', 'twilio', 'google-ads'],
  ['slack', 'okta', 'adroll', 'datocms'],
  ['perplexity', 'adobe-fonts', 'airtable', 'databricks'],
  ['google-workspace', 'gong', 'azure', 'aws'],
] as const;

type PlateProps = {
  top: number;
  className?: string;
  plateClassName?: string;
  plateStyle?: CSSProperties;
  children?: ReactNode;
};

function IsoPlate({
  top,
  className,
  plateClassName,
  plateStyle,
  children,
}: PlateProps) {
  return (
    <div
      className={cn(
        'absolute flex items-center justify-center will-change-transform',
        className,
      )}
      style={{ left: 20, top, width: 416, height: 241 }}
    >
      <div
        className={cn(
          'h-[241px] w-[241px] flex-none rounded-14',
          plateClassName,
        )}
        style={{ transform: ISO_TRANSFORM, ...plateStyle }}
      >
        {children}
      </div>
    </div>
  );
}

function IsoLabel({
  x,
  y,
  className,
  children,
}: {
  x: number;
  y: number;
  className?: string;
  children: ReactNode;
}) {
  return (
    <p
      className={cn(
        // text-[10px] (not text-10): tailwind-merge can't tell the custom
        // text-10 size from color classes like text-grayscale-01 and would
        // drop it as a "conflict", leaving the label at the inherited 16px.
        'absolute whitespace-nowrap bg-charcoal px-6 text-[10px] font-[500] leading-[1.13] tracking-[0.6px] text-grayscale-01',
        className,
      )}
      style={{
        left: x,
        top: y,
        transform:
          'translate(-50%, -50%) rotate(-29.25deg) skewX(30deg) scaleY(0.87)',
      }}
    >
      {children}
    </p>
  );
}

type LineProps = {
  x: number;
  y: number;
  height: number;
  className?: string;
};

/**
 * All lines are SVGs with `vector-effect: non-scaling-stroke` so their 1px
 * strokes stay 1px on screen when the scene's scale transform shrinks the
 * canvas on mobile.
 */
const LINE_W = 8;
const lineSvgProps = (x: number, y: number, height: number) =>
  ({
    'aria-hidden': true,
    width: LINE_W,
    height,
    viewBox: `0 0 ${LINE_W} ${height}`,
    fill: 'none',
    style: { left: x - LINE_W / 2, top: y },
  }) as const;

/** Dashed depth guide with open chevron arrowheads at both ends. */
function GuideLine({ x, y, height, className }: LineProps) {
  const cx = LINE_W / 2;
  return (
    <svg
      {...lineSvgProps(x, y, height)}
      className={cn(
        'absolute origin-top overflow-visible text-grayscale-04',
        className,
      )}
    >
      <path
        d={`M${cx} 4 V${height - 4}`}
        stroke="currentColor"
        strokeDasharray="3 3"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d={`M${cx - 2.8} 3.2 L${cx} 0.4 L${cx + 2.8} 3.2`}
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d={`M${cx - 2.8} ${height - 3.2} L${cx} ${height - 0.4} L${cx + 2.8} ${height - 3.2}`}
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/** Solid note connector with filled triangle arrowheads at both ends. */
function ConnectorLine({ x, y, height, className }: LineProps) {
  const cx = LINE_W / 2;
  return (
    <svg
      {...lineSvgProps(x, y, height)}
      className={cn(
        'absolute origin-top overflow-visible text-grayscale-04',
        className,
      )}
    >
      <path
        d={`M${cx} 3 V${height - 3}`}
        stroke="currentColor"
        vectorEffect="non-scaling-stroke"
      />
      <path d={`M${cx} 0 l2.9 5 h-5.8 Z`} fill="currentColor" />
      <path d={`M${cx} ${height} l2.9 -5 h-5.8 Z`} fill="currentColor" />
    </svg>
  );
}

/** Lime data-flow arrow with an open chevron head. */
function FlowArrow({
  x,
  y,
  height,
  direction,
  className,
}: LineProps & { direction: 'up' | 'down' }) {
  const cx = LINE_W / 2;
  const head =
    direction === 'up'
      ? `M${cx - 2.8} 3.2 L${cx} 0.4 L${cx + 2.8} 3.2`
      : `M${cx - 2.8} ${height - 3.2} L${cx} ${height - 0.4} L${cx + 2.8} ${height - 3.2}`;
  return (
    <svg
      {...lineSvgProps(x, y, height)}
      className={cn('absolute overflow-visible text-electric', className)}
    >
      <path
        d={`M${cx} ${direction === 'up' ? 1.4 : 0} V${direction === 'up' ? height : height - 1.4}`}
        stroke="currentColor"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d={head}
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function Note({
  top,
  width,
  bulletClassName,
  className,
  children,
}: {
  top: number;
  width: number;
  bulletClassName?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn('absolute', className)}
      style={{ left: 450, top, width }}
    >
      <span
        aria-hidden
        className={cn(
          'absolute left-0 top-10 h-12 w-12 rounded-full border-1 border-grayscale-04',
          bulletClassName,
        )}
      />
      <p className="pl-22 text-left text-24 font-[250] leading-130 text-cloud">
        {children}
      </p>
    </div>
  );
}

/** Fallbacks for the five annotation callouts when unset in Sanity. */
const DEFAULT_NOTES = [
  'Transcend enables data activation by encoding data permissions',
  'directly into the systems',
  'that process customer data',
  'With Sombra, secure-by-design architecture protecting',
  'even the most sensitive data use cases with end-to-end encryption',
];

export function SombraStackAnimation({ notes }: { notes?: string[] | null }) {
  const noteText = (index: number) => notes?.[index] || DEFAULT_NOTES[index];
  const wrapperRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    let lastScale = -1;
    return observeElementResize(el, ([entry]) => {
      if (!entry) return;
      const next = Math.min(entry.contentRect.width / SCENE_W, 1);
      if (Math.abs(next - lastScale) < 0.001) return;
      lastScale = next;
      setScale(next);
    });
  }, []);

  // Sections above this one mount client-side (media-query gated animations
  // etc.), growing the page well after ScrollTrigger measures its pin start.
  // Re-measure whenever the document height changes or the trigger would
  // engage thousands of pixels too early and the scrub range would already
  // be spent by the time the section scrolls into view.
  useEffect(() => {
    let lastHeight = document.body.scrollHeight;
    let timeoutId: ReturnType<typeof setTimeout>;

    const disconnect = observeElementResize(document.body, () => {
      const height = document.body.scrollHeight;
      if (Math.abs(height - lastHeight) < 2) return;
      lastHeight = height;
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => ScrollTrigger.refresh(), 150);
    });

    return () => {
      disconnect();
      clearTimeout(timeoutId);
    };
  }, []);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();

      // The whole section (headline + scene + buttons) gets pinned. The
      // parent ref isn't assigned yet when this layout effect runs, so
      // resolve it from the DOM instead.
      const pinTarget = sceneRef.current?.closest<HTMLElement>(
        '[data-sombra-section]',
      );
      if (!pinTarget) return;

      const scene = sceneRef.current;
      const wrapper = wrapperRef.current;
      if (!scene || !wrapper) return;

      // Reverting the previous run restores the inline height ScrollTrigger
      // captured back then (the pre-scale 543px on first load), and React
      // won't re-apply it since its vdom already holds the current value.
      // Re-assert it so the fit measurements below see the real height.
      wrapper.style.height = `${SCENE_H * scale}px`;

      const pinGroup = pinTarget.querySelector<HTMLElement>(
        '[data-sombra-pin-group]',
      );

      mm.add('(prefers-reduced-motion: no-preference)', () => {
        // Pin as much of the section as fits the viewport: the whole thing
        // (headline + graphic + buttons) on tall screens, the headline +
        // graphic group on shorter ones, and the graphic alone as a last
        // resort - so the animation always plays fully in view.
        let ctx: gsap.Context | undefined;

        // While pinned, the pin-spacer sits inside `el` and inflates it by
        // the pin distance; subtract that to recover the natural height.
        const naturalHeight = (el: HTMLElement) => {
          let height = el.offsetHeight;
          const spacer = el.querySelector<HTMLElement>('.pin-spacer');
          const pinned = spacer?.firstElementChild as HTMLElement | null;
          if (spacer && pinned) {
            height -= spacer.offsetHeight - pinned.offsetHeight;
          }
          return height;
        };

        const headerHeight = () =>
          document.querySelector('header')?.offsetHeight ?? 0;

        const resolvePinElement = () => {
          // Pinning the whole section puts its top edge at the viewport top,
          // so its padding must also be deep enough that the eyebrow clears
          // the sticky header; otherwise fall through to the group tier,
          // which is pinned below the header instead.
          const header = headerHeight();
          if (
            naturalHeight(pinTarget) <= window.innerHeight &&
            parseFloat(getComputedStyle(pinTarget).paddingTop) >= header
          ) {
            return pinTarget;
          }
          if (
            pinGroup &&
            naturalHeight(pinGroup) <= window.innerHeight - header
          ) {
            return pinGroup;
          }
          return wrapper;
        };

        let pinElement = resolvePinElement();

        const build = () => {
          ctx?.revert();
          ctx = gsap.context(() => {
            const pinWholeSection = pinElement === pinTarget;

            // Beat 1 - the Transcend plate and first note play as a one-shot
            // entry animation when the scene scrolls into view, before the
            // pin engages.
            gsap
              .timeline({
                defaults: { ease: 'power2.out' },
                scrollTrigger: {
                  trigger: wrapper,
                  start: 'top 60%',
                  // Replay the entry whenever the scene re-enters from above;
                  // animate back out when scrolling up past the trigger.
                  toggleActions: 'restart none none reverse',
                },
              })
              .from('.js-plate-transcend', {
                y: -60,
                autoAlpha: 0,
                duration: 0.8,
              })
              .from(
                '.js-note-1',
                { autoAlpha: 0, y: 20, duration: 0.6 },
                '-=0.4',
              );

            const tl = gsap.timeline({
              defaults: { ease: 'power1.out' },
              scrollTrigger: {
                trigger: pinElement,
                // Sit the pinned block below the sticky header, centered in
                // the leftover space but never more than 48px down - taller
                // phones would otherwise leave a large gap above the eyebrow.
                start: pinWholeSection
                  ? 'top top'
                  : () => {
                      const header = headerHeight();
                      const free =
                        window.innerHeight - header - pinElement.offsetHeight;
                      const gap = Math.min(Math.max(free / 2, 0), 48);
                      return `top ${header + gap}`;
                    },
                end: '+=200%',
                pin: pinElement,
                // The section is a flex column; ScrollTrigger disables pin
                // spacing for flex parents by default, but padding works
                // fine in a column layout so force it back on.
                pinSpacing: true,
                scrub: 0.8,
                anticipatePin: 1,
                invalidateOnRefresh: true,
              },
            });

            addBeats(tl);
          }, scene);
        };

        const addBeats = (tl: gsap.core.Timeline) => {
          tl
            // Beat 2 - "Your Systems" plate slides in underneath (beat 1 runs
            // as an entry animation before the pin, see build())
            .addLabel('systems', '+=0.2')
            .from(
              '.js-plate-systems',
              { y: 90, autoAlpha: 0, duration: 1 },
              'systems',
            )
            .from(
              '.js-edge-line, .js-front-line',
              { scaleY: 0, autoAlpha: 0, duration: 1 },
              'systems',
            )
            .from(
              '.js-label-systems',
              { autoAlpha: 0, duration: 0.4 },
              'systems+=0.6',
            )
            .from('.js-line-a1', { scaleY: 0, duration: 0.8 }, 'systems')
            .from(
              '.js-note-2',
              { autoAlpha: 0, y: 20, duration: 0.8 },
              'systems+=0.4',
            )

            // Beat 3 - "Customer Data" plate slides in underneath
            .addLabel('customers', '+=0.4')
            .from(
              '.js-plate-customers',
              { y: 90, autoAlpha: 0, duration: 1 },
              'customers',
            )
            .from(
              '.js-edge-3, .js-front-c',
              { scaleY: 0, autoAlpha: 0, duration: 0.8 },
              'customers+=0.3',
            )
            .from(
              '.js-label-customers',
              { autoAlpha: 0, duration: 0.4 },
              'customers+=0.6',
            )
            .from('.js-line-a2', { scaleY: 0, duration: 0.8 }, 'customers')
            .from(
              '.js-note-3',
              { autoAlpha: 0, y: 20, duration: 0.8 },
              'customers+=0.4',
            )

            // Beat 4 - Sombra slots in between; annotations swap over
            .addLabel('sombra', '+=0.5')
            .to('.js-group-a', { autoAlpha: 0, duration: 0.5 }, 'sombra')
            .from(
              '.js-plate-sombra',
              { y: -110, autoAlpha: 0, duration: 1.2 },
              'sombra+=0.2',
            )
            .from(
              '.js-label-sombra',
              { autoAlpha: 0, duration: 0.4 },
              'sombra+=1',
            )
            .from('.js-line-b1', { scaleY: 0, duration: 0.8 }, 'sombra+=0.9')
            // The continuous guides swap for split segments with arrowheads
            // flanking the Sombra plate's vertices as the plate lands
            .to(
              '.js-edge-line, .js-front-line',
              { autoAlpha: 0, duration: 0.3 },
              'sombra+=0.7',
            )
            .from(
              '.js-edge-split, .js-front-split',
              { autoAlpha: 0, duration: 0.4 },
              'sombra+=0.9',
            )
            .from(
              '.js-flow-up',
              { autoAlpha: 0, y: 12, duration: 0.5, stagger: 0.15 },
              'sombra+=1',
            )
            .from(
              '.js-flow-down',
              { autoAlpha: 0, y: -12, duration: 0.5, stagger: 0.15 },
              'sombra+=1.2',
            )
            .from(
              '.js-note-s1',
              { autoAlpha: 0, y: 20, duration: 0.8 },
              'sombra+=0.8',
            )
            .from(
              '.js-note-s2',
              { autoAlpha: 0, y: 20, duration: 0.8 },
              'sombra+=1.2',
            );

          // The button row lives outside the scene, so resolve it from the
          // section and reveal it once the stack has fully assembled. No
          // trailing hold - the pin releases as the fade completes so the
          // next section starts scrolling up right away.
          const buttonRow = pinTarget.querySelector('.js-sombra-buttons');
          if (buttonRow) {
            tl.from(buttonRow, { autoAlpha: 0, y: 24, duration: 0.6 });
          }
        };

        build();

        // Rebuild only when the fit decision flips; reverting first restores
        // the DOM so the from-tweens re-capture clean end states. Runs on
        // window resize and whenever the graphic settles to its measured
        // scale (which happens after this effect on first load).
        let resizeTimeout: ReturnType<typeof setTimeout>;
        const onResize = () => {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(() => {
            const next = resolvePinElement();
            if (next !== pinElement) {
              pinElement = next;
              build();
            }
          }, 150);
        };
        window.addEventListener('resize', onResize);

        return () => {
          window.removeEventListener('resize', onResize);
          clearTimeout(resizeTimeout);
          ctx?.revert();
        };
      });
    },
    {
      scope: sceneRef,
      // The first render measures the scene at scale 1; rebuild everything
      // once the fitted scale lands so pin sizing and the fit decision use
      // the real graphic height.
      dependencies: [scale],
      revertOnUpdate: true,
    },
  );

  return (
    <div
      ref={wrapperRef}
      // overflow-x-clip: before hydration the scene renders unscaled at its
      // full 846px canvas width, which would otherwise widen the page and
      // allow side-scrolling on mobile until the measured scale kicks in.
      className="w-[calc(100%+24px)] max-w-[846px] overflow-x-clip m:w-full"
      style={{ height: SCENE_H * scale }}
    >
      <div
        ref={sceneRef}
        className="relative origin-top-left"
        style={{
          width: SCENE_W,
          height: SCENE_H,
          transform: `scale(${scale})`,
        }}
      >
        {/* ===== Stack ===== */}

        {/* Side depth guides, Transcend down to Your Systems. Continuous
            until the Sombra plate slides in, then swapped for split segments
            with arrowheads flanking the Sombra plate's side vertices */}
        <GuideLine x={27} y={156} height={147} className="js-edge-line" />
        <GuideLine x={429} y={156} height={147} className="js-edge-line" />
        {/* Split gap sized for the 1.04-scaled Sombra plate, whose side
            vertices reach ~8px further out than the other plates' */}
        <GuideLine x={27} y={156} height={17} className="js-edge-split" />
        <GuideLine x={429} y={156} height={17} className="js-edge-split" />
        <GuideLine x={27} y={202} height={101} className="js-edge-split" />
        <GuideLine x={429} y={202} height={101} className="js-edge-split" />
        {/* Side guides between Your Systems and Customer Data */}
        <GuideLine x={27} y={324} height={90} className="js-edge-3" />
        <GuideLine x={429} y={324} height={90} className="js-edge-3" />

        {/* Customer Data plate */}
        <IsoPlate
          top={303}
          className="js-plate-customers"
          plateClassName="border-1 border-grayscale-03"
        >
          <div className="flex h-full w-full items-center justify-center">
            <div className="grid grid-cols-4 gap-12">
              {Array.from({ length: 16 }).map((_, i) => (
                <span
                  key={i}
                  className="flex h-37 w-37 items-center justify-center rounded-full border-1 border-grayscale-03"
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src="/sombra/user.svg" alt="" className="h-20 w-20" />
                </span>
              ))}
            </div>
          </div>
        </IsoPlate>

        {/* Your Systems plate */}
        <IsoPlate
          top={194}
          className="js-plate-systems"
          plateClassName="border-1 border-grayscale-02 bg-charcoal"
        >
          <div className="flex h-full w-full items-center justify-center">
            <div className="grid grid-cols-4 gap-11">
              {SYSTEM_LOGOS.flat().map((logo) => (
                <span
                  key={logo}
                  className="flex h-44 w-44 items-center justify-center rounded-7 border-1 border-grayscale-02"
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={`/sombra/${logo}.svg`}
                    alt=""
                    className="h-18 w-18"
                  />
                </span>
              ))}
            </div>
          </div>
        </IsoPlate>

        {/* Front depth guide down the stack's centerline. Sits above the
            Systems/Customer plates but below the Sombra/Transcend plates.
            Continuous from the Transcend vertex to the Systems front vertex
            until Sombra lands, then swapped for the split segments. */}
        <GuideLine x={228} y={266} height={154} className="js-front-line" />
        <GuideLine x={228} y={318} height={102} className="js-front-split" />
        {/* Front guide down the Customer Data plate */}
        <GuideLine x={228} y={429} height={105} className="js-front-c" />

        {/* Sombra plate. Slightly larger than the others so its edges clear
            the Transcend plate's rounded corners instead of pinching the
            visible lime band at the vertices */}
        <IsoPlate
          top={67}
          className="js-plate-sombra"
          plateClassName="relative overflow-clip bg-electric-07"
          plateStyle={{ transform: `${ISO_TRANSFORM} scale(1.04)` }}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/sombra/lock.svg"
            alt=""
            className="absolute h-39 w-39 opacity-80"
            style={{ left: 198, top: 199 }}
          />
        </IsoPlate>

        {/* Transcend plate */}
        <IsoPlate
          top={24}
          className="js-plate-transcend"
          plateClassName="flex items-center justify-center"
          plateStyle={{ backgroundColor: TRANSCEND_PLATE_COLOR }}
        >
          <TranscendLogo iconOnly className="w-78 text-cloud" />
        </IsoPlate>

        {/* Data-flow arrows through the stack + the front guide segment that
            lies on the Sombra plate's face; all reveal with the Sombra beat.
            Lighter gray so the dashes read against the lime plate. */}
        <GuideLine
          x={228}
          y={266}
          height={36}
          className="js-front-split text-grayscale-02"
        />
        {/* Up arrows stop at the Sombra plate's underside, which sits ~5px
            lower since the plate is scaled 1.04; bottoms stay anchored on
            the systems tiles */}
        <FlowArrow
          x={135}
          y={259}
          height={56}
          direction="up"
          className="js-flow-up"
        />
        <FlowArrow
          x={186}
          y={291}
          height={51}
          direction="up"
          className="js-flow-up"
        />
        <FlowArrow
          x={274}
          y={291}
          height={53}
          direction="up"
          className="js-flow-up"
        />
        <FlowArrow
          x={136}
          y={381}
          height={42}
          direction="down"
          className="js-flow-down"
        />
        <FlowArrow
          x={186}
          y={410}
          height={41}
          direction="down"
          className="js-flow-down"
        />
        <FlowArrow
          x={274}
          y={409}
          height={38}
          direction="down"
          className="js-flow-down"
        />

        {/* Plate labels */}
        <IsoLabel x={340} y={381} className="js-label-systems">
          YOUR SYSTEMS
        </IsoLabel>
        {/* Wraps to two centered lines. Unlike the other labels this one sits
            fully outside its plate edge, so no charcoal notch - the stroke
            stays continuous above it with a clear gap */}
        <IsoLabel
          x={344}
          y={494}
          className="js-label-customers w-[86px] whitespace-normal bg-transparent text-center"
        >
          CUSTOMER DATA
        </IsoLabel>
        <IsoLabel x={348} y={254} className="js-label-sombra text-electric">
          SOMBRA
        </IsoLabel>

        {/* ===== Annotations ===== */}
        <div>
          <div className="js-group-a">
            <Note
              top={130}
              width={300}
              className="js-note-1"
              bulletClassName="border-cipher-04"
            >
              {noteText(0)}
            </Note>
            <ConnectorLine
              x={456}
              y={156}
              height={140}
              className="js-line-a1"
            />
            <Note top={294} width={310} className="js-note-2">
              {noteText(1)}
            </Note>
            <ConnectorLine x={456} y={326} height={92} className="js-line-a2" />
            <Note top={415} width={352} className="js-note-3">
              {noteText(2)}
            </Note>
          </div>

          <div className="js-group-b">
            <Note
              top={172}
              width={300}
              className="js-note-s1"
              bulletClassName="border-electric"
            >
              {noteText(3)}
            </Note>
            <ConnectorLine
              x={456}
              y={196}
              height={160}
              className="js-line-b1"
            />
            <Note top={353} width={300} className="js-note-s2">
              {noteText(4)}
            </Note>
          </div>
        </div>
      </div>
    </div>
  );
}
